/**
 * ENGAGE-PRICING-SPLIT-1 — split out of EngagementCreate.tsx to help bring it
 * back under the FILE_SIZE_POLICY 400-line hard stop after the server-quote
 * rewrite added lines back. Pure presentational: all pricing/tier state
 * (including the live buildQuote() call) still lives in EngagementCreate.tsx
 * and is passed in as props — this component renders it, nothing more.
 */
import type { QuoteResponse } from "@/api/scope";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PRICING_ENABLED } from "@/lib/pricing";
import { TIER_FINDING_COUNTS, TIER_LABELS, type ScopeTier } from "@/lib/qoeCatalogue.generated";
import { fmt, SIZING_BASES, type SizingBasis } from "@/lib/engagementIntake";

const TIER_ORDER: ScopeTier[] = ["lite", "focused", "full"];

interface TierFeeCardProps {
  sizingBasis: SizingBasis;
  setSizingBasis: (v: SizingBasis) => void;
  sizingInput: string;
  setSizingInput: (v: string) => void;
  sizingDollars: number;
  ebitdaInput: string;
  setEbitdaInput: (v: string) => void;
  ebitdaEstimate: number;
  ebitdaDollars: number;
  sizedFromProxy: boolean;
  isSuperAdmin: boolean;
  autoTier: ScopeTier;
  effectiveTier: ScopeTier;
  tierOverride: ScopeTier | "auto";
  setTierOverride: (v: ScopeTier | "auto") => void;
  isOverridden: boolean;
  overrideReason: string;
  setOverrideReason: (v: string) => void;
  quote: QuoteResponse | null;
  quoteLoading: boolean;
  quoteError: string | null;
  baseFee: number;
}

export default function TierFeeCard({
  sizingBasis, setSizingBasis, sizingInput, setSizingInput, sizingDollars,
  ebitdaInput, setEbitdaInput, ebitdaEstimate, ebitdaDollars, sizedFromProxy,
  isSuperAdmin, autoTier, effectiveTier, tierOverride, setTierOverride,
  isOverridden, overrideReason, setOverrideReason,
  quote, quoteLoading, quoteError, baseFee,
}: TierFeeCardProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{PRICING_ENABLED ? "Tier & Fee Estimate" : "Scope Tier"}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <Label htmlFor="sizing_basis">Size this engagement by *</Label>
          <Select value={sizingBasis} onValueChange={v => { if (v) setSizingBasis(v as SizingBasis); }}>
            <SelectTrigger id="sizing_basis">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(Object.keys(SIZING_BASES) as SizingBasis[]).map(k => (
                <SelectItem key={k} value={k}>{SIZING_BASES[k].label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor="sizing_m">{SIZING_BASES[sizingBasis].field} *</Label>
          <Input
            id="sizing_m"
            type="number"
            step="0.1"
            min="0"
            placeholder="e.g. 12.5"
            value={sizingInput}
            onChange={e => setSizingInput(e.target.value)}
          />
          <p className="mt-1 text-xs text-gray-500">
            {sizingDollars > 0 ? `${fmt(sizingDollars)} — ` : ""}
            {SIZING_BASES[sizingBasis].help}
          </p>
        </div>

        <div>
          <Label htmlFor="ebitda_m">Adjusted EBITDA Estimate (in $M) — optional</Label>
          <Input
            id="ebitda_m"
            type="number"
            step="0.1"
            min="0"
            placeholder="Leave blank if you don't have one yet"
            value={ebitdaInput}
            onChange={e => setEbitdaInput(e.target.value)}
          />
          <p className="mt-1 text-xs text-gray-500">
            {ebitdaEstimate > 0 ? `${fmt(ebitdaEstimate)} — ` : ""}
            Estimate for sizing only — actual EBITDA is calculated later from
            the client's financial statements.
          </p>
        </div>

        {ebitdaDollars > 0 && (
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">Automatic tier</span>
              <span className={`rounded-full px-3 py-0.5 text-sm font-semibold ${
                autoTier === "lite"
                  ? "bg-muted text-accent-foreground border border-accent/30"
                  : "bg-accent text-accent-foreground"
              }`}>
                {quoteLoading && !quote
                  ? "Pricing…"
                  : `${TIER_LABELS[autoTier]} — ${TIER_FINDING_COUNTS[autoTier]}-point`}
              </span>
            </div>
            {sizedFromProxy && (
              <p className="text-xs text-gray-500">
                Sized from {SIZING_BASES[sizingBasis].label.toLowerCase()} at an
                EBITDA equivalent of {fmt(ebitdaDollars)}. Revisit the tier once
                the normalization workspace has the client's actual figures.
              </p>
            )}
            {isSuperAdmin && (
              <>
                <div>
                  <Label htmlFor="tier_override">CPA Tier Override</Label>
                  <Select
                    value={tierOverride}
                    onValueChange={v => { if (v) { setTierOverride(v as ScopeTier | "auto"); setOverrideReason(""); } }}
                  >
                    <SelectTrigger id="tier_override">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="auto">Auto ({TIER_LABELS[autoTier]})</SelectItem>
                      {TIER_ORDER.map(t => (
                        <SelectItem key={t} value={t}>
                          {TIER_LABELS[t]} — {TIER_FINDING_COUNTS[t]}-point
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {isOverridden && (
                  <div>
                    <Label htmlFor="override_reason">Override Reason *</Label>
                    <Input
                      id="override_reason"
                      placeholder="Reason for overriding the automatic tier assignment…"
                      value={overrideReason}
                      onChange={e => setOverrideReason(e.target.value)}
                      required
                    />
                    <p className="mt-1 text-xs text-amber-700">Override will be logged to audit trail.</p>
                  </div>
                )}
              </>
            )}
            {PRICING_ENABLED && (
              <div className="flex items-center justify-between border-t pt-3">
                <span className="text-sm text-gray-600">Base fee ({TIER_LABELS[effectiveTier]})</span>
                <span className="font-semibold text-gray-900">
                  {quoteLoading ? "…" : quote ? fmt(baseFee) : "—"}
                </span>
              </div>
            )}
            {quoteError && (
              <p className="text-xs text-red-600">Pricing unavailable: {quoteError}</p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
